import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

export type LiveEvent =
  | { type: 'ping' }
  | { type: 'service.added'; name: string }
  | { type: 'service.updated'; name: string }
  | { type: 'service.removed'; id: string }
  | { type: 'node.added'; name: string }
  | { type: 'node.updated'; name: string }
  | { type: 'node.removed'; id: string }
  | { type: 'stack.added'; name: string }
  | { type: 'echo'; payload: unknown }

export type LiveStatus = 'connecting' | 'open' | 'closed'

function wsUrl(): string {
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
  return `${proto}://${window.location.host}/ws`
}

export function useLiveEvents(): LiveStatus {
  const [status, setStatus] = useState<LiveStatus>('connecting')
  const qc = useQueryClient()
  const wsRef = useRef<WebSocket | null>(null)
  const retryRef = useRef(0)

  useEffect(() => {
    let closed = false

    function connect() {
      const ws = new WebSocket(wsUrl())
      wsRef.current = ws
      setStatus('connecting')

      ws.onopen = () => {
        setStatus('open')
        retryRef.current = 0
      }
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data) as LiveEvent
          switch (msg.type) {
            case 'service.added':
            case 'service.updated':
            case 'service.removed':
              qc.invalidateQueries({ queryKey: ['cluster', 'services'] })
              qc.invalidateQueries({ queryKey: ['cluster', 'stacks'] })
              break
            case 'node.added':
            case 'node.updated':
            case 'node.removed':
              qc.invalidateQueries({ queryKey: ['cluster', 'nodes'] })
              break
            case 'stack.added':
              qc.invalidateQueries({ queryKey: ['cluster', 'stacks'] })
              break
            default:
              break
          }
        } catch {
          /* ignore */
        }
      }
      ws.onclose = () => {
        setStatus('closed')
        wsRef.current = null
        if (!closed) {
          const delay = Math.min(30_000, 1000 * 2 ** retryRef.current)
          retryRef.current++
          setTimeout(connect, delay)
        }
      }
      ws.onerror = () => {
        try {
          ws.close()
        } catch {
          /* noop */
        }
      }
    }

    connect()
    return () => {
      closed = true
      wsRef.current?.close()
    }
  }, [qc])

  return status
}
